import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  formatDate,
  formatRelativeTime,
  getCountryFlag,
} from "../lib/utils";
import { Activity, Monitor, Smartphone, Tablet, Mail } from "lucide-react";

const DeviceIcon = ({ device }) => {
  switch (device?.toLowerCase()) {
    case "mobile":
      return <Smartphone className="h-4 w-4" />;
    case "tablet":
      return <Tablet className="h-4 w-4" />;
    default:
      return <Monitor className="h-4 w-4" />;
  }
};

const EventsPage = () => {
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [deviceFilter, setDeviceFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data: campaigns } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => {
      const response = await api.get("/campaigns");
      return response.data;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["events", campaignFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: page.toString(), limit: "20" });
      if (campaignFilter !== "all") {
        params.append("campaignId", campaignFilter);
      }
      const response = await api.get(`/events?${params}`);
      return response.data;
    },
  });

  // Filter events by device on client side
  const filteredEvents = data?.events?.filter((event) => {
    if (deviceFilter === "all") return true;
    return event.device === deviceFilter;
  });

  return (
    <div className="space-y-8" data-testid="events-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-heading">Eventi</h1>
        <p className="text-muted-foreground">Log completo di tutte le aperture email</p>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-100">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Campagna:</span>
              <Select
                value={campaignFilter}
                onValueChange={(value) => {
                  setCampaignFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-48" data-testid="campaign-filter">
                  <SelectValue placeholder="Tutte le campagne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le campagne</SelectItem>
                  {campaigns?.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Dispositivo:</span>
              <Select
                value={deviceFilter}
                onValueChange={(value) => {
                  setDeviceFilter(value);
                }}
              >
                <SelectTrigger className="w-40" data-testid="device-filter">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="desktop">Desktop</SelectItem>
                  <SelectItem value="mobile">Mobile</SelectItem>
                  <SelectItem value="tablet">Tablet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card className="bg-white border border-slate-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Attività ({data?.total || 0} eventi totali)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : filteredEvents?.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="events-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Campagna
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Apertura
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Dispositivo
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Paese
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Città
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        IP
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((event, index) => (
                      <tr
                        key={event.id}
                        className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors animate-fade-in"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary text-sm font-medium">
                                {event.email?.charAt(0).toUpperCase() || "?"}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{event.email || "N/D"}</p>
                              {event.name && (
                                <p className="text-xs text-muted-foreground">{event.name}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm">{event.campaignName || "N/D"}</span>
                          {event.isFirstOpen && (
                            <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">
                              Prima
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm font-medium">{formatRelativeTime(event.openedAt)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(event.openedAt)}
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <DeviceIcon device={event.device} />
                            <span className="capitalize">{event.device || "N/D"}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {event.country ? (
                            <>
                              {getCountryFlag(event.country)} {event.country}
                            </>
                          ) : (
                            "N/D"
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {event.city || "N/D"}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground font-mono">
                          {event.ipAddress || "N/D"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data?.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Precedente
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Pagina {page} di {data.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                    disabled={page === data.totalPages}
                  >
                    Successiva
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <img
                src="https://images.unsplash.com/photo-1586957960362-65815d739527?crop=entropy&cs=srgb&fm=jpg&q=85&w=300"
                alt="Nessun evento"
                className="w-32 h-32 object-cover rounded-2xl mx-auto mb-6 opacity-60"
              />
              <p className="text-muted-foreground font-medium">Nessun evento trovato</p>
              <p className="text-sm text-muted-foreground mt-1">
                Gli eventi appariranno quando i destinatari apriranno le email
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EventsPage;
